import type { ReactElement } from 'react';
import type { StoryVersion } from '@/types/story';
import type { User } from '@/types/user';
import type { LucideIcon } from 'lucide-react';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import PageLayout from '@/components/PageLayout';
import AuthModal from '@/components/AuthModal';
import VersionBadge from '@/components/VersionBadge';
import HeroCarousel from '@/components/HeroCarousel';
import ImagePreview from '@/components/ImagePreview';
import ClickableAuthor from '@/components/ClickableAuthor';
import HorizontalStoryRow from '@/components/HorizontalStoryRow';
import HorizontalSeriesRow from '@/components/HorizontalSeriesRow';
import HorizontalCreatorRow from '@/components/HorizontalCreatorRow';
import FloatingCreateButton from '@/components/FloatingCreateButton';
import ActiveJobsBanner from '@/components/ActiveJobsBanner';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Sparkles, TrendingUp, Clock, Heart, Eye, User as UserIcon, ChevronRight, Mountain, Moon, PawPrint, Users, Home, Laugh, Leaf, BookOpen, Plus, Star, Loader2, Library, Crown, Film, GraduationCap, BookMarked } from 'lucide-react';
import { getBookTypeLabel } from '@/lib/utils';
import { STORY_CATEGORIES } from '@/constants/categories';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Interfaces for data structures
interface StoryPage {
  page_number: number;
  image_url?: string;
}

interface HomeStory {
  id: string;
  title: string;
  thumbnail?: string;
  age_range: string;
  version?: StoryVersion;
  is_pro_story?: boolean;
  is_public?: boolean;
  is_favorited?: boolean;
  author_name?: string;
  author_picture?: string;
  user_id: string;
  pages?: StoryPage[];
  page_count?: number;
  favorites_count?: number;
  views_count?: number;
  created_at: string;
  series_id?: string;
  series_title?: string;
  series_is_public?: boolean;
  has_animations?: boolean;
}

interface HomeSeries {
  id: string;
  title: string;
  description?: string;
  cover_image_url?: string;
  thumbnail?: string;
  age_range: string;
  book_count?: number;
  author_name?: string;
  book_summaries?: Array<{ thumbnail?: string }>;
}

interface LastReadStory {
  id: string;
  title?: string;
  page?: number;
}

interface Filter {
  id: string;
  label: string;
  icon: LucideIcon;
  requiresAuth?: boolean;
  isFollowing?: boolean;
  isPro?: boolean;
  isSeries?: boolean;
  isAnimated?: boolean;
}

interface Job {
  id: string;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  progress?: number;
  queue_position?: number;
  title?: string;
  prompt?: string;
  thumbnail_url?: string;
  created_at: string;
}

interface Creator {
  user_id: string;
  name: string;
  handle?: string;
  picture?: string;
  bio?: string;
  followers_count: number;
  story_count: number;
  is_premium: boolean;
  is_following: boolean;
}

// Helper to get full image URL (handles both local /static/ paths and external URLs)
const getImageUrl = (url: string | undefined | null): string | null => {
  if (!url) return null;
  if (url.startsWith('/static/')) {
    return `${BACKEND_URL}${url}`;
  }
  return url;
};

const HomePage = (): ReactElement => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialFilter = searchParams.get('filter') || 'for-you';
  const [featured, setFeatured] = useState<HomeStory[]>([]);
  const [trending, setTrending] = useState<HomeStory[]>([]);
  const [newStories, setNewStories] = useState<HomeStory[]>([]);
  const [featuredSeries, setFeaturedSeries] = useState<HomeSeries[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [showAuthModal, setShowAuthModal] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<string>(initialFilter);
  const [lastReadStory, setLastReadStory] = useState<LastReadStory | null>(null);
  const [offset, setOffset] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [featuredCreators, setFeaturedCreators] = useState<Creator[]>([]);
  const PAGE_SIZE = 20; // Load more per batch for smoother scrolling
  const loaderRef = useRef<HTMLDivElement | null>(null);
  const prevActiveJobCount = useRef<number>(0);
  // Grace period counter to prevent jobs from disappearing on API hiccups
  const pollGraceCounter = useRef<number>(0);

  // Infinite scroll observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasMore && !loading && !loadingMore) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }

    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [hasMore, loading, loadingMore, offset]);

  // Special filters (non-category)
  const specialFilters: Filter[] = [
    { id: 'for-you', label: 'For You', icon: Sparkles },
    { id: 'following', label: 'Following', icon: Users, requiresAuth: true, isFollowing: true },
    { id: 'pro', label: 'Pro', icon: Star, isPro: true },
    { id: 'series', label: 'Series', icon: Library, isSeries: true },
    { id: 'animated', label: 'Animated', icon: Film, isAnimated: true },
    { id: 'trending', label: 'Trending', icon: TrendingUp },
    { id: 'new', label: 'New', icon: Clock },
  ];

  // Category filters from centralized source
  const categoryFilters: Filter[] = STORY_CATEGORIES.map(cat => ({
    id: cat.value,
    label: cat.label,
    icon: cat.icon,
  }));

  const allFilters: Filter[] = [...specialFilters, ...categoryFilters];

  // Filter out auth-required filters when not logged in
  const filters = user
    ? allFilters
    : allFilters.filter(f => !f.requiresAuth);

  // Handle successful auth - reload page data
  const handleAuthSuccess = (): void => {
    setShowAuthModal(false);
    loadData(0, true);
  };

  // Update activeFilter when URL changes
  useEffect(() => {
    const filterFromUrl = searchParams.get('filter');
    if (filterFromUrl && filterFromUrl !== activeFilter) {
      setActiveFilter(filterFromUrl);
    }
  }, [searchParams]);

  useEffect(() => {
    // Reset pagination when filter changes
    setFeatured([]);
    setOffset(0);
    setHasMore(true);
    loadData(0, true);
  }, [activeFilter]);

  const loadData = async (currentOffset: number = 0, isReset: boolean = false): Promise<void> => {
    if (isReset) {
      setLoading(true);
    } else {
      setLoadingMore(true);
    }

    try {
      // Check auth (only on initial load)
      if (isReset) {
        try {
          const userRes = await axios.get<User>(`${API}/auth/me`, { withCredentials: true });
          setUser(userRes.data);

          // Get last read story from localStorage (user-specific)
          if (userRes.data?.user_id) {
            const lastRead = localStorage.getItem(`lastReadStory_${userRes.data.user_id}`);
            if (lastRead) {
              try {
                setLastReadStory(JSON.parse(lastRead) as LastReadStory);
              } catch (e) {
                localStorage.removeItem(`lastReadStory_${userRes.data.user_id}`);
              }
            }
          }
        } catch (error) {
          setUser(null);
        }
      }

      // Load stories based on filter
      const params = new URLSearchParams();
      params.append('limit', String(PAGE_SIZE));
      params.append('offset', String(currentOffset));

      if (activeFilter === 'following') {
        params.append('following_filter', 'true');
        params.append('sort', 'new');
      } else if (activeFilter === 'pro') {
        params.append('pro_filter', 'pro');
        params.append('sort', 'trending');
      } else if (activeFilter === 'for-you' || activeFilter === 'trending') {
        params.append('sort', 'trending');
      } else if (activeFilter === 'new') {
        params.append('sort', 'new');
      } else if (activeFilter === 'series') {
        params.append('series_filter', 'series');
        params.append('sort', 'trending');
      } else if (activeFilter === 'animated') {
        params.append('animated_filter', 'animated');
        params.append('sort', 'trending');
      } else {
        params.append('category', activeFilter);
        params.append('sort', 'trending');
      }

      const response = await axios.get<HomeStory[]>(`${API}/stories/explore?${params.toString()}`, {
        withCredentials: true
      });

      const fetchedStories = response.data;

      // Check if there are more stories to load
      setHasMore(fetchedStories.length === PAGE_SIZE);

      // Append or replace stories (deduplicate by ID to prevent duplicates)
      if (isReset) {
        setFeatured(fetchedStories);
      } else {
        setFeatured(prev => {
          const existingIds = new Set(prev.map(s => s.id));
          const uniqueNewStories = fetchedStories.filter(s => !existingIds.has(s.id));
          return [...prev, ...uniqueNewStories];
        });
      }

      // Load sidebar data only on initial load
      if (isReset) {
        // Load trending separately
        const trendingRes = await axios.get<HomeStory[]>(`${API}/stories/featured?limit=10`, {
          withCredentials: true
        });
        setTrending(trendingRes.data);

        // Load new stories
        const newRes = await axios.get<HomeStory[]>(`${API}/stories/explore?sort=new&limit=10`, {
          withCredentials: true
        });
        setNewStories(newRes.data);

        // Load featured series
        try {
          const seriesRes = await axios.get<{ series?: HomeSeries[] }>(`${API}/v1/series/public/explore?sort=popular&limit=3`, {
            withCredentials: true
          });
          if (seriesRes.data?.series) {
            setFeaturedSeries(seriesRes.data.series);
          }
        } catch (e) {
          console.error('Failed to load featured series:', e);
        }

        // Load featured creators
        try {
          const creatorsRes = await axios.get<Creator[]>(`${API}/creators/featured?limit=10`, {
            withCredentials: true
          });
          setFeaturedCreators(creatorsRes.data);
        } catch (e) {
          console.error('Failed to load featured creators:', e);
        }
      }

    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Failed to load:', error.message);
      } else {
        console.error('Failed to load:', error);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = (): void => {
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    loadData(newOffset, false);
  };

  // Load active jobs for progress indicator
  const loadJobs = async (): Promise<void> => {
    if (!user) return;
    try {
      const [activeJobsRes, allJobsRes] = await Promise.all([
        axios.get<{ jobs: Job[] }>(`${API}/v1/jobs/mine?include_completed=false`, { withCredentials: true }),
        axios.get<{ jobs: Job[] }>(`${API}/v1/jobs/mine?include_completed=true&limit=10`, { withCredentials: true })
      ]);

      const allJobs = allJobsRes.data.jobs || [];
      const active = (activeJobsRes.data.jobs || []).filter(j =>
        j.status === 'queued' || j.status === 'processing'
      );

      // Grace period logic: only apply when jobs disappear unexpectedly (not completed)
      if (active.length === 0 && activeJobs.length > 0) {
        const previousJobIds = activeJobs.map(j => j.id);
        const completedOrFailed = allJobs.filter(j =>
          previousJobIds.includes(j.id) && (j.status === 'completed' || j.status === 'failed')
        );

        if (completedOrFailed.length === activeJobs.length) {
          pollGraceCounter.current = 0;
        } else {
          pollGraceCounter.current += 1;
          if (pollGraceCounter.current < 5) {
            console.log(`HomePage job grace period: poll ${pollGraceCounter.current}/5`);
            return;
          }
        }
      } else {
        pollGraceCounter.current = 0;
      }

      setActiveJobs(active);
    } catch (error) {
      // On error, keep previous state
      console.error('Failed to load jobs (keeping previous state):', error);
    }
  };

  // Load jobs when user is authenticated
  useEffect(() => {
    if (user) {
      loadJobs();
    }
  }, [user]);

  // Poll for job updates when there are active jobs
  useEffect(() => {
    if (!user || activeJobs.length === 0) return;

    const pollInterval = setInterval(() => {
      loadJobs();
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [user, activeJobs.length]);

  // Reload stories when a job completes
  useEffect(() => {
    if (prevActiveJobCount.current > 0 && activeJobs.length < prevActiveJobCount.current) {
      loadData(0, true);
    }
    prevActiveJobCount.current = activeJobs.length;
  }, [activeJobs.length]);

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const handleFollowCreator = async (creatorId: string, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!user) {
      setShowAuthModal(true);
      return;
    }

    // Optimistic update
    setFeaturedCreators(prev => prev.map(c => {
      if (c.user_id === creatorId) {
        return {
          ...c,
          is_following: !c.is_following,
          followers_count: c.is_following ? c.followers_count - 1 : c.followers_count + 1
        };
      }
      return c;
    }));

    try {
      await axios.post(`${API}/profile/${creatorId}/follow`, {}, { withCredentials: true });
    } catch (error) {
      // Revert on error
      setFeaturedCreators(prev => prev.map(c => {
        if (c.user_id === creatorId) {
          return {
            ...c,
            is_following: !c.is_following,
            followers_count: c.is_following ? c.followers_count - 1 : c.followers_count + 1
          };
        }
        return c;
      }));
      console.error('Failed to follow/unfollow:', error);
    }
  };

  const renderStoryCard = (story: HomeStory): ReactElement => {
    // Extract page image URLs for preview
    const previewImages = story.pages?.map(page => page.image_url).filter(Boolean) as string[] || [];

    return (
      <div
        data-testid={`story-card-${story.id}`}
        className="rounded-xl overflow-hidden shadow-sm story-card-hover cursor-pointer flex flex-col"
        style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
        onClick={() => navigate(`/story/${story.id}`)}
      >
        {/* Thumbnail with hover preview - aspect ratio matches book covers */}
        <div className="aspect-[4/3] flex-shrink-0 overflow-hidden relative rounded-t-xl" style={{ background: 'linear-gradient(135deg, var(--st-border-light) 0%, var(--st-border-medium) 100%)' }}>
          {story.thumbnail ? (
            <ImagePreview
              thumbnail={story.thumbnail}
              previewImages={previewImages}
              alt={story.title}
            >
              {/* Series indicator icon with tooltip */}
              {story.series_title && story.series_is_public && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={(e) => { e.stopPropagation(); navigate(`/series/public/${story.series_id}`); }}
                      className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors z-10"
                    >
                      <Library className="w-3 h-3" style={{ color: '#4F46E5' }} />
                    </button>
                  </TooltipTrigger>
                  {/* @ts-expect-error - TooltipContent side prop from Radix primitives */}
                  <TooltipContent side="bottom">
                    <p className="text-xs">Part of: {story.series_title}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {/* Animated indicator icon with tooltip */}
              {story.has_animations && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className={`absolute top-1.5 ${story.series_title && story.series_is_public ? 'right-8' : 'right-1.5'} w-6 h-6 rounded-full bg-amber-500/90 flex items-center justify-center z-10`}
                    >
                      <Film className="w-3 h-3 text-white" />
                    </div>
                  </TooltipTrigger>
                  {/* @ts-expect-error - TooltipContent side prop from Radix primitives */}
                  <TooltipContent side="bottom">
                    <p className="text-xs">Animated Pages</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {/* Favorite indicator overlay */}
              {story.is_favorited && (
                <div className={`absolute top-1.5 ${(story.series_title && story.series_is_public) || story.has_animations ? (story.series_title && story.series_is_public && story.has_animations ? 'right-[3.75rem]' : 'right-8') : 'right-1.5'} w-6 h-6 rounded-full bg-white/90 flex items-center justify-center`}>
                  <Heart className="w-3 h-3 fill-current" style={{ color: 'var(--st-primary)' }} />
                </div>
              )}
              {/* Version badge on image */}
              <div className="absolute top-1.5 left-1.5 flex items-center gap-1 z-10">
                <VersionBadge version={story.version} isProStory={story.is_pro_story} size="small" />
              </div>
            </ImagePreview>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <BookOpen className="w-10 h-10" style={{ color: 'var(--st-primary-hover)' }} />
            </div>
          )}
        </div>

      <div className="p-2.5 flex-1 flex flex-col">
        <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-heading)' }}>
            {getBookTypeLabel(story.age_range)}
          </span>
          {story.page_count && story.page_count > 0 && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold" style={{ background: 'var(--st-border-light)', color: 'var(--st-text-muted)' }}>
              {story.page_count}p
            </span>
          )}
        </div>

        <h3 className="font-bold text-sm mb-1.5 line-clamp-2 leading-tight" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>
          {story.title}
        </h3>

        <div className="mb-2">
          <ClickableAuthor
            userId={story.user_id}
            name={story.author_name}
            picture={story.author_picture}
            size="small"
          />
        </div>

        <div className="flex items-center justify-between text-[10px] mt-auto" style={{ color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
          <div className="flex items-center gap-2">
            <span className="flex items-center gap-0.5">
              <Heart className="w-3 h-3" fill={story.is_favorited ? '#C2410C' : 'none'} stroke={story.is_favorited ? '#C2410C' : 'currentColor'} />
              {story.favorites_count || 0}
            </span>
            {story.views_count && story.views_count > 0 && (
              <span className="flex items-center gap-0.5">
                <Eye className="w-3 h-3" />
                {story.views_count}
              </span>
            )}
          </div>
          <span>{formatDate(story.created_at)}</span>
        </div>
      </div>
    </div>
    );
  };

  return (
    <PageLayout>
      <div className="flex-1">
        <div className="max-w-screen-2xl mx-auto px-4 py-4 md:py-6 md:px-8 2xl:px-12">
          {/* Hero Carousel */}
          <HeroCarousel />

          {/* Active Jobs Banner (if any) */}
          {activeJobs.length > 0 && (
            <ActiveJobsBanner jobs={activeJobs} />
          )}

          {/* Filter Tabs - Keep all 17 */}
          <div className="flex items-center gap-1 p-1 mb-4 md:mb-6 overflow-x-auto scrollbar-hide rounded-xl" style={{ background: 'var(--st-bg-muted)' }}>
            {filters.map((filter) => {
              const Icon = filter.icon;
              const isActive = activeFilter === filter.id;
              const isPro = filter.isPro;
              const isSeries = filter.isSeries;
              const isFollowing = filter.isFollowing;
              const isAnimated = filter.isAnimated;
              // Determine colors based on filter type
              const activeColor = isPro ? '#9B59B6' : isSeries ? '#4F46E5' : isFollowing ? '#059669' : isAnimated ? '#FF8C61' : 'var(--st-text-heading)';
              const inactiveColor = isPro ? '#9B59B6' : isSeries ? '#6366F1' : isFollowing ? '#10B981' : isAnimated ? '#FF8C61' : 'var(--st-text-muted)';
              const iconColor = isPro ? '#9B59B6' : isSeries ? '#6366F1' : isFollowing ? '#10B981' : isAnimated ? '#FF8C61' : 'var(--st-primary)';
              return (
                <button
                  key={filter.id}
                  data-testid={`filter-${filter.id}`}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`flex-shrink-0 flex items-center gap-1.5 px-4 py-2.5 rounded-lg font-semibold text-sm whitespace-nowrap transition-all duration-200 ${
                    isActive ? 'bg-white dark:bg-white/10 shadow-sm' : 'hover:bg-white/50 dark:hover:bg-white/10'
                  }`}
                  style={{
                    fontFamily: 'Quicksand, sans-serif',
                    color: isActive ? activeColor : inactiveColor
                  }}
                >
                  <Icon className="w-4 h-4" style={{ color: iconColor }} />
                  {filter.label}
                </button>
              );
            })}
          </div>

          {/* Continue Reading Card - Only shows if user has a last read story */}
          {lastReadStory && (
            <div
              className="mb-6 p-3 rounded-xl flex items-center justify-between cursor-pointer hover:shadow-md transition-all"
              style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}
              onClick={() => navigate(`/story/${lastReadStory.id}?continue=true`)}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)' }}
                >
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p
                    className="text-sm font-bold"
                    style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
                  >
                    Continue Reading
                  </p>
                  <p
                    className="text-xs"
                    style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}
                  >
                    {lastReadStory.title ? `"${lastReadStory.title.slice(0, 30)}${lastReadStory.title.length > 30 ? '...' : ''}"` : 'Your story'}
                    {lastReadStory.page && lastReadStory.page > 0 && ` • Page ${lastReadStory.page}`}
                  </p>
                </div>
              </div>
              <ChevronRight className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--st-primary)' }} />
            </div>
          )}

          {/* Netflix-style Content Rows - Only show on "For You" home view */}
          {activeFilter === 'for-you' && (
            <div className="space-y-6 md:space-y-8 mb-8">
              <HorizontalStoryRow
                title="Trending Now"
                icon={TrendingUp}
                iconColor="var(--st-primary)"
                stories={trending}
                onSeeAll={() => setActiveFilter('trending')}
              />

              <HorizontalStoryRow
                title="Fresh Stories"
                icon={Clock}
                iconColor="var(--st-primary)"
                stories={newStories}
                onSeeAll={() => setActiveFilter('new')}
              />

              <HorizontalSeriesRow
                title="Featured Series"
                series={featuredSeries}
                onSeeAll={() => navigate('/explore?tab=series')}
              />

              <HorizontalCreatorRow
                title="Creators to Follow"
                creators={featuredCreators}
                onFollowToggle={handleFollowCreator}
                onSeeAll={() => navigate('/explore?tab=creators')}
              />
            </div>
          )}

          {/* Main Stories Grid - Full Width */}
          <section>
            <h2
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}
            >
              {activeFilter === 'for-you' ? 'All Stories' : `${filters.find(f => f.id === activeFilter)?.label || 'All'} Stories`}
            </h2>

            {loading ? (
              <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-center">
                  <Sparkles className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: 'var(--st-primary)' }} />
                  <p style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Loading stories...</p>
                </div>
              </div>
            ) : featured.length === 0 ? (
              <div className="text-center py-12 rounded-2xl shadow-sm" style={{ background: 'var(--st-bg-surface)', border: '1px solid var(--st-border-light)' }}>
                {activeFilter === 'following' ? (
                  <>
                    <Users className="w-16 h-16 mx-auto mb-4 animate-float" style={{ color: '#10B981' }} />
                    <p className="font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>No stories from people you follow</p>
                    <p className="text-sm mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Follow some creators to see their stories here!</p>
                    <button
                      onClick={() => setActiveFilter('trending')}
                      className="px-6 py-2 rounded-full font-bold btn-press"
                      style={{ background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: 'white' }}
                    >
                      Discover Creators
                    </button>
                  </>
                ) : (
                  <>
                    <BookOpen className="w-16 h-16 mx-auto mb-4 animate-float" style={{ color: 'var(--st-border-medium)' }} />
                    <p className="font-bold mb-2" style={{ fontFamily: 'Chewy, cursive', color: 'var(--st-text-heading)' }}>No stories found</p>
                    <p className="text-sm mb-4" style={{ fontFamily: 'Quicksand, sans-serif', color: 'var(--st-text-muted)' }}>Be the first to create in this category!</p>
                    <button
                      onClick={() => navigate('/create')}
                      className="px-6 py-2 rounded-full font-bold btn-press"
                      style={{ background: 'linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)', color: 'white' }}
                    >
                      Create Story
                    </button>
                  </>
                )}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
                  {featured.map((story) => (
                    <div key={story.id}>{renderStoryCard(story)}</div>
                  ))}
                </div>

                {/* Infinite scroll loader */}
                {hasMore && (
                  <div ref={loaderRef} className="flex justify-center py-8">
                    {loadingMore && (
                      <div className="flex items-center gap-2" style={{ color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
                        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--st-primary)' }} />
                        <span className="text-sm">Loading more stories...</span>
                      </div>
                    )}
                  </div>
                )}

                {/* End of stories indicator */}
                {!hasMore && featured.length > 0 && (
                  <div className="text-center py-8">
                    <p className="text-sm" style={{ color: 'var(--st-text-muted)', fontFamily: 'Quicksand, sans-serif' }}>
                      You've seen all the stories!
                    </p>
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      </div>

      {/* Floating Create Button */}
      <FloatingCreateButton user={user} onClick={() => navigate('/create')} />

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onSuccess={handleAuthSuccess}
      />
    </PageLayout>
  );
};

export default HomePage;
